import type { ReactNode } from "react";
import {
  SITE_URL,
  baseCrumbs,
  sharedRelated,
  orgNode,
  faqJsonld,
  breadcrumbJsonld,
  webPageNode,
  type LearnFAQ,
} from "./learnShared";
import type { LearnPageData } from "./learnShared";

const P = (s: ReactNode): ReactNode => <p>{s}</p>;

const p8Faqs: LearnFAQ[] = [
  {
    question: "What is Spravato used for?",
    answer:
      "Spravato (esketamine) nasal spray is FDA-approved for two uses in adults: treatment-resistant depression, either on its own or together with an oral antidepressant, and depressive symptoms in adults with major depressive disorder who have acute suicidal thoughts or behavior, together with an oral antidepressant. Treatment-resistant depression generally means major depression that has not improved adequately after at least two antidepressant trials in the current episode. Any other use is off-label.",
  },
  {
    question:
      'Are the "Spravato horror stories" online something to worry about?',
    answer:
      "Most alarming accounts describe dissociation, which is the most common effect of esketamine, reported by 41% of trial patients. Feeling detached from your body or surroundings, dizziness, nausea, sedation, and a temporary rise in blood pressure are expected and are exactly why the FDA requires at least two hours of monitoring in a certified clinic. These effects usually peak around 40 minutes and resolve before discharge. Serious harm is uncommon when contraindications are screened. Tell your team if the experience is distressing; dose and supportive measures can be adjusted.",
  },
  {
    question: "Is Spravato covered by insurance?",
    answer:
      "Often, but not automatically. Most commercial plans and Medicare cover Spravato for its FDA-approved indications after prior authorization that documents a diagnosis of major depression and prior antidepressant trials. The drug must be given at a REMS-certified site, and plans may bill the drug and the monitoring separately. Coverage, copays, and site requirements vary by plan, so insurance verification before the first session is essential. Neuro Wellness TMS Centers of America can verify benefits but cannot promise coverage.",
  },
  {
    question: "What should I do during a Spravato treatment?",
    answer:
      "Plan to rest. Arrive with a driver, avoid food for two hours and liquids for 30 minutes beforehand, and bring headphones with calm music or an eye mask if that helps you relax. After self-administering the spray under supervision, you recline in a monitored room for at least two hours while staff check blood pressure and how you feel. Reading or working is usually hard during the first hour. You cannot drive until the next day after a restful sleep.",
  },
  {
    question: "How is Spravato different from TMS?",
    answer:
      "Spravato is a drug; TMS is a device. Spravato is an NMDA-receptor-blocking nasal spray that causes dissociation and sedation during a two-hour monitored session and requires a ride home. TMS uses magnetic pulses to stimulate the prefrontal cortex while you sit awake in a chair, with no sedation and no driving restriction, over about 30 to 36 sessions (or five days for SAINT). Spravato is FDA-approved; TMS devices are FDA-cleared. Both are options for depression that has not responded to antidepressants.",
  },
  {
    question: "How long does a Spravato session take?",
    answer:
      "Plan for about two and a half to three hours. That includes a pre-dose blood pressure check, self-administration with five-minute pauses between devices, at least two hours of required monitoring, and a discharge assessment. During the induction phase this happens twice a week for four weeks, then weekly, then every one to two weeks.",
  },
  {
    question: "Does Spravato have off-label uses?",
    answer:
      "Some prescribers use Spravato off-label for conditions such as bipolar depression, PTSD, or anxiety disorders. Off-label prescribing is legal, but these uses are not FDA-approved, the evidence for them is thinner than for treatment-resistant depression, and insurance generally does not cover them. Ask your clinician directly whether a proposed use is on-label and what evidence supports it.",
  },
  {
    question: "Is Spravato the same as ketamine infusions?",
    answer:
      "No. Spravato is esketamine, one half of the ketamine molecule, given as a nasal spray at fixed doses and FDA-approved for treatment-resistant depression under a REMS program. IV ketamine uses the full racemic drug, is given by infusion at weight-based doses that vary by clinic, and is off-label for depression because ketamine is FDA-approved only as an anesthetic. Both cause dissociation and require monitoring; insurance is more likely to cover Spravato.",
  },
];

export const page8: LearnPageData = {
  slug: "spravato-vs-tms-for-depression",
  title: "Spravato (Esketamine) vs TMS for Depression Explained",
  description:
    "What Spravato (esketamine) nasal spray is, its FDA-approved uses, side effects, REMS monitoring, and how it compares to TMS and SAINT for resistant depression.",
  canonical: "/learn/spravato-vs-tms-for-depression",
  h1: "What Is Spravato® (Esketamine), and How Does It Compare to TMS?",
  breadcrumbLabel: "Spravato vs TMS",
  crumbs: baseCrumbs("Spravato vs TMS"),
  shortAnswer:
    "Spravato® (esketamine) is an FDA-approved nasal spray for treatment-resistant depression, given in a monitored clinic session of about two hours. TMS is an FDA-cleared device treatment delivered awake with no sedation. Both are options when antidepressants have not worked. Call Neuro Wellness TMS Centers of America at 954-827-9793.",
  keyTakeaways: [
    "Spravato® is FDA-approved (a drug); TMS devices are FDA-cleared (devices).",
    "Spravato® causes dissociation and requires a 2-hour monitored session and a ride home; TMS has no sedation and no driving restriction.",
    "Spravato® is FDA-approved for TRD and for depressive symptoms with acute suicidal ideation.",
    "A Spravato® induction phase is twice weekly for 4 weeks, then tapers; TMS is ~30–36 sessions over 4–6 weeks (or 5 days for SAINT®).",
    "Spravato® is not the same as IV ketamine — esketamine is FDA-approved for TRD; IV ketamine for depression is off-label.",
  ],
  sections: [
    {
      heading: "What is Spravato® (esketamine)?",
      body: (
        <>
          {P(
            "Spravato® (esketamine) is an FDA-approved nasal spray for treatment-resistant depression in adults. It is an NMDA-receptor antagonist — a different mechanism from standard antidepressants, which mostly target serotonin, norepinephrine, or dopamine. It is self-administered in a certified clinic under supervision, and you are monitored for at least two hours afterward.",
          )}
          {P(
            "Spravato® is FDA-approved for two uses: treatment-resistant depression (on its own or with an oral antidepressant), and depressive symptoms in adults with major depressive disorder who have acute suicidal thoughts or behavior (with an oral antidepressant). Any other use is off-label.",
          )}
        </>
      ),
    },
    {
      heading: "Regulatory status: FDA-approved vs FDA-cleared",
      body: (
        <div className="my-6 rounded-xl border border-medium-blue/30 bg-medium-blue/5 p-5">
          <p>
            <strong>Regulatory status.</strong> Spravato (esketamine) is
            FDA-approved — it went through the drug approval process and is a
            Schedule III controlled substance under a REMS (Risk Evaluation and
            Mitigation Strategy) program. TMS devices, including the Magnus
            Neuromodulation System with SAINT Technology, are FDA-cleared
            through the 510(k) pathway. Both are legitimate treatments for
            depression that has not responded to antidepressants, but they are
            regulated differently because one is a drug and the other is a
            device.
          </p>
        </div>
      ),
    },
    {
      heading: "What a Spravato® session is like",
      body: (
        <>
          {P(
            "You arrive with a driver, avoid food for two hours and liquids for 30 minutes beforehand, and self-administer the nasal spray under staff supervision with five-minute pauses between devices. Afterward you recline in a monitored room for at least two hours while staff check your blood pressure and how you feel. The most common effect is dissociation — feeling detached from your body or surroundings — which usually peaks around 40 minutes and resolves before discharge.",
          )}
          {P(
            "You cannot drive until the next day after a restful sleep. During the induction phase, sessions happen twice a week for four weeks, then weekly, then every one to two weeks.",
          )}
        </>
      ),
    },
    {
      heading: "How Spravato® compares to TMS",
      body: (
        <>
          {P(
            "The fundamental difference: Spravato® is a drug that acts chemically on NMDA receptors and causes dissociation and sedation; TMS is a device that uses magnetic pulses to stimulate the prefrontal cortex with no sedation and no driving restriction.",
          )}
          {P(
            "Spravato® sessions are about two and a half to three hours with monitoring and require a ride home. TMS sessions are about 3 to 40 minutes (depending on protocol), and you drive yourself home. A standard TMS course is about 30 to 36 sessions over four to six weeks; SAINT® compresses TMS into five days. Spravato® and TMS are both options for depression that has not responded to antidepressants — the right choice depends on your history, preferences, and insurance.",
          )}
        </>
      ),
    },
    {
      heading: "Spravato® vs IV ketamine",
      body: (
        <>
          {P(
            "Spravato® is esketamine (one enantiomer of ketamine), given as a nasal spray at fixed doses and FDA-approved for treatment-resistant depression under a REMS program. IV ketamine uses the full racemic drug, is given by infusion at weight-based doses that vary by clinic, and is off-label for depression because ketamine is FDA-approved only as an anesthetic.",
          )}
          {P(
            "Both cause dissociation and require monitoring. Insurance is more likely to cover Spravato® than off-label IV ketamine, because Spravato® has an FDA-approved indication for depression.",
          )}
        </>
      ),
    },
  ],
  faqs: p8Faqs,
  sources: [
    {
      citation:
        "FDA Label, Spravato (esketamine) Nasal Spray. Janssen Pharmaceuticals, Inc.",
      url: "https://www.accessdata.fda.gov/drugsatfda_docs/label/2025/211243s019lbl.pdf",
    },
    {
      citation:
        "Popova V, et al. Efficacy and safety of flexibly dosed esketamine nasal spray combined with a newly initiated oral antidepressant in adult patients with treatment-resistant depression. Am J Psychiatry. 2019;176(6):428-438.",
    },
    {
      citation:
        "Cole EJ, et al. Stanford Neuromodulation Therapy (SNT): A Double-Blind Randomized Controlled Trial. Am J Psychiatry. 2022;179(2):132-141.",
      url: "https://doi.org/10.1176/appi.ajp.2021.20101429",
    },
  ],
  schemaJson: {
    "@context": "https://schema.org",
    "@graph": [
      webPageNode({
        id: `${SITE_URL}/learn/spravato-vs-tms-for-depression#webpage`,
        name: "What Is Spravato® (Esketamine), and How Does It Compare to TMS?",
        url: `${SITE_URL}/learn/spravato-vs-tms-for-depression`,
        description:
          "What Spravato (esketamine) nasal spray is, its FDA-approved uses, side effects, REMS monitoring, and how it compares to TMS and SAINT for resistant depression.",
        about: [
          {
            "@type": "MedicalTherapy",
            name: "SPRAVATO® (esketamine) nasal spray treatment",
            description:
              "FDA-approved esketamine nasal spray for treatment-resistant depression and for depressive symptoms with acute suicidal ideation, administered in a certified clinic under a REMS monitoring program. Prescription-only; Schedule III controlled substance; NMDA receptor antagonist (Janssen Pharmaceuticals, Inc.).",
            url: "https://www.accessdata.fda.gov/drugsatfda_docs/label/2025/211243s019lbl.pdf",
          },
          {
            "@type": "MedicalTherapy",
            name: "Transcranial Magnetic Stimulation (TMS)",
            alternateName: "TMS therapy",
            procedureType: "Noninvasive",
            relevantSpecialty: {
              "@type": "MedicalSpecialty",
              name: "Psychiatry",
            },
          },
          {
            "@type": "MedicalCondition",
            name: "Treatment-resistant depression",
          },
          {
            "@type": "MedicalCondition",
            name: "Major depressive disorder",
            code: {
              "@type": "MedicalCode",
              code: "D003865",
              codingSystem: "MeSH",
            },
          },
        ],
      }),
      orgNode,
      {
        "@type": "MedicalTherapy",
        name: "Stanford Accelerated Intelligent Neuromodulation Therapy (SAINT)",
        alternateName: "Magnus Neuromodulation System with SAINT Technology",
        procedureType: "Noninvasive",
      },
      faqJsonld(p8Faqs, `${SITE_URL}/learn/spravato-vs-tms-for-depression#faq`),
      breadcrumbJsonld(baseCrumbs("Spravato vs TMS")),
    ],
  },
  relatedLinks: sharedRelated,
};
